import DataTable from "../../Components/Common/datatable/DataTable";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoPopover from "../../Components/Common/poper/InfoPopover";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import ApiService from "../../utils/fetch";
import { MessagePopup } from "../../Components/Common/messagePopup/MessagePopup";
import { Skeleton } from "@mui/material";
import ButtonComponent from "../../Components/Common/button/Button";
import Modal from "../../Components/Common/Modal/Modal";
import { Timestamp } from "../../Components/Common/DateFormatter";

function UserManagement() {
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [modalfilteredData, setModalFilteredData] = useState([]);

  const [emailOnlyFirstName, setEmailOnlyFirstName] = useState("");
  const [emailOnlyLastName, setEmailOnlyLastName] = useState("");
  const [emailOnlyEmail, setEmailOnlyEmail] = useState("");

  const [open, setOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [isRoleValid, setIsRoleValid] = useState(false);

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setModalFilteredData([]);
    setSelectedRole("");
    setTimeout(() => {
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.sizeColumnsToFit();
      }
    }, 200);
  };

  useEffect(() => {
    if (!selectedRole) {
      setModalFilteredData([]);
    }
  }, [selectedRole]);

  useEffect(() => {
    postData();
  }, []);

  const postData = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get(
        "/v1/admin/userManagement/user-details",
      );
      setFilteredData(response.data);
    } catch (error) {
      console.error("Error posting data", error);
    } finally {
      setLoading(false);
    }
  };

  const update = (params) => {
    if (!params?.data?.roles) {
      setIsRoleValid(false);
      return;
    }
    // Navigate if roles is valid
    setIsRoleValid(true);
    navigate("/user-details", { state: { data: params.data } });
  };

  const remove = async (params) => {
    const result = await MessagePopup({
      title: "Confirm User Deletion",
      html: `<p>Are you sure you want to delete the user <strong>${params.data.firstName} ${params.data.lastName}</strong>?</p>`,
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    });

    if (!result.isConfirmed) return;

    const rowNode = params.node;
    const transaction = { remove: [params.data] };
    params.api.applyTransaction(transaction);

    try {
      await ApiService.put(
        `/v1/admin/userManagement/delete?user_details_id=${params.data.id}`,
      );

      MessagePopup({
        icon: "success",
        title: "User Deleted",
        message: "User deleted successfully.",
        confirmButtonText: "Ok",
        confirmButtonColor: "#000000",
      });
    } catch (error) {
      console.error("Error deleting user:", error);
      MessagePopup({
        icon: "error",
        title: "Deletion Failed",
        message: "Failed to delete the user. Please try again.",
        confirmButtonText: "Ok",
        confirmButtonColor: "#000000",
      });

      // Re-add the row if the deletion API call fails
      params.api.applyTransaction({
        add: [params.data],
        addIndex: rowNode.rowIndex,
      });
    }
  };

  const AddUser = async (params) => {
    const result = await MessagePopup({
      title: "Confirm User Add",
      html: `<p>Are you sure you want to Add the user <strong>${params.data.firstName} ${params.data.lastname}</strong>?</p>`,
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    });

    if (!result.isConfirmed) return;

    const requestBody = {
      firstName: params.data.firstName,
      lastname: params.data.lastname,
      loginEmailId: params.data.loginEmailId,
      notificationEmailId: params.data.notificationEmailId,
    };
    try {
      const response = await ApiService.post(
        `/v1/admin/userManagement/addNewUser`,
        requestBody,
      );
      MessagePopup({
        icon: "success",
        title: "User Added",
        message: response.data,
        confirmButtonText: "Ok",
        confirmButtonColor: "#000000",
      });
      postData(); // Refresh the user list after adding
      handleClose();
    } catch (error) {
      console.error("Error adding user:", error);
      MessagePopup({
        icon: "error",
        title: "Addition Failed",
        message: "User Already Exists.",
        confirmButtonText: "Ok",
        confirmButtonColor: "#000000",
      });
    }
  };
  const columnDefinition = [
    {
      field: "firstName",
      headerName: "User Name",
      sortable: true,
      unSortIcon: true,
      width: 220,
      minWidth: 220,
      valueGetter: (params) => {
        const { firstName, lastName } = params.data;
        return `${firstName || ""} ${lastName || ""}`;
      },
      cellRenderer: (params) => {
        const display = `${params.data.firstName || ""} ${
          params.data.lastName || ""
        }`; // Combine firstName and lastName

        return (
          <Tooltip title={display} arrow>
            <div>
              {/* Render main cell content */}
              <span>
                {params.data.firstName || ""} {params.data.lastName || ""}
              </span>

              {/* Conditionally render "New user" badge */}
              {params.data.newUser && (
                <span
                  style={{
                    background: "#6AC9CE",
                    color: "#000",
                    borderRadius: "4px",
                    padding: "2px 4px",
                    marginLeft: "8px",
                    fontSize: "11px",
                  }}
                >
                  New user
                </span>
              )}
            </div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "email",
      headerName: "Email",
      sortable: true,
      unSortIcon: true,
      width: 220,
      minWidth: 220,
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.email}`} arrow>
          <div>{params.data.email}</div>
        </Tooltip>
      ),
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "roles",
      headerName: "Roles",
      sortable: true,
      unSortIcon: true,
      width: 140,
      minWidth: 140,
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.roles}`} arrow>
          <div>{params.data.roles}</div>
        </Tooltip>
      ),
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    // {
    //   field: "bottlerCustomerPlantCount",
    //   headerName: "Active Combination",
    //   sortable: true,
    //   unSortIcon: true,
    //   width: 240,
    //   minWidth: 240,
    //   filter: "agMultiColumnFilter",
    //   filterParams: {
    //     filters: [
    //       {
    //         filter: "agTextColumnFilter",
    //       },
    //       {
    //         filter: "agSetColumnFilter",
    //       },
    //     ],
    //   },
    // },
    {
      field: "createdAt",
      headerName: "Created Date",
      width: 200,
      minWidth: 200,
      sortable: true,
      unSortIcon: true,
      filterParams: {
        comparator: function (filterDate, cellValue) {
          if (!cellValue) return -1;

          const cellDate = new Date(cellValue);
          // Strip time and compare only YYYY-MM-DD
          const cellDateOnly = new Date(
            cellDate.getFullYear(),
            cellDate.getMonth(),
            cellDate.getDate(),
          );

          return cellDateOnly.getTime() - filterDate.getTime();
        },
        filterOptions: ["equals", "inRange"],
        defaultOption: "equals",
        buttons: ["apply", "reset"],
        closeOnApply: true,
        onApply: (params) => {
          params.api.hidePopupMenu();
        },
        onReset: (params) => {
          params.api.hidePopupMenu();
        },
      },

      filter: "agDateColumnFilter",
      valueFormatter: (params) => {
        return Timestamp(params.value);
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      minWidth: 110,
      cellRenderer: (params) => (
        <div>
          <EditIcon
            style={{
              cursor: !isRoleValid ? "pointer" : "", // Pointer for enabled, not-allowed for disabled
              marginRight: "10px",
              color: !isRoleValid ? "black" : "grey", // Use grey for disabled
            }}
            onClick={() => !isRoleValid && update(params)} // Prevent clicking if disabled
          />
          <DeleteIcon
            style={{ cursor: "pointer" }}
            onClick={() => remove(params)}
          />
        </div>
      ),
    },
    {
      field: "applicableDataElementsList",
      headerName: "Data Elements",
      sortable: true,
      unSortIcon: true,
      width: 190,
      minWidth: 190,
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.applicableDataElementsList}`} arrow>
          <div>{params.data.applicableDataElementsList}</div>
        </Tooltip>
      ),
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
  ];

  useEffect(() => {
    if (gridRef.current && gridRef.current.api && filteredData.length > 0) {
      gridRef.current.api.sizeColumnsToFit();
    }
  }, [filteredData]);

  const onGrid = (params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.sizeColumnsToFit();
    } else {
      console.error("Grid API is not available.");
    }
  };

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  const modalColumns = [
    {
      field: "firstName",
      headerName: "User Name",
      width: 370,
      sortable: true,
      unSortIcon: true,
      cellRenderer: (params) => (
        <div>{`${params.data.firstName} ${params.data.lastname}`}</div>
      ),
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
        closeOnApply: true,
        onApply: (params) => {
          params.api.hidePopupMenu();
        },
        onReset: (params) => {
          params.api.hidePopupMenu();
        },
      },
      filter: "agTextColumnFilter",
    },
    {
      field: "loginEmailId",
      headerName: "Email",
      width: 380,
      sortable: true,
      unSortIcon: true,
      filterParams: {
        filterOptions: ["contains", "startsWith", "endsWith"],
        defaultOption: "contains",
        buttons: ["apply", "reset"],
        closeOnApply: true,
        onApply: (params) => {
          params.api.hidePopupMenu();
        },
        onReset: (params) => {
          params.api.hidePopupMenu();
        },
      },
      filter: "agTextColumnFilter",
    },
    {
      field: "add",
      headerName: "Add",
      width: 130,
      cellRenderer: (params) => (
        <AddCircleIcon
          onClick={() => AddUser(params)}
          style={{ cursor: "pointer" }}
        />
      ),
    },
  ];

  const roletype = [
    "Bottler",
    "CSR",
    "DemandPlanner",
    "Admin",
    "Super User",
    "IT",
    "Emailonly",
  ];

  const searchFunction = async () => {
    if (!selectedRole) {
      MessagePopup({
        icon: "warning",
        title: "Role Required",
        message: "Please select a role to search.",
        confirmButtonText: "Ok",
        confirmButtonColor: "#000000",
      });
      return;
    }
    if (selectedRole === "Emailonly") {
      // Do nothing, just show the manual input form
      setModalFilteredData([]); // Clear modal data if needed
      return;
    }
    try {
      const response = await ApiService.get(
        `/v1/admin/userManagement/fetch-new-user-details?group_name=${selectedRole}`,
      );
      setModalFilteredData(response.data);
      if (response.data.length === 0) {
        MessagePopup({
          icon: "info",
          title: "No Users Found",
          message: `No users found for the role "${selectedRole}".`,
          confirmButtonText: "Ok",
          confirmButtonColor: "#000000",
        });
      }
    } catch (error) {
      console.error("Error removing role:", error);
      MessagePopup({
        icon: "error",
        title: "Search Failed",
        message: "Failed to fetch user details. Please try again.",
        confirmButtonText: "Ok",
        confirmButtonColor: "#000000",
      });
    }
  };

  const paginationModal = true;
  const paginationModalPageSize = 5;
  const paginationModalPageSizeSelector = [5, 10, 20];

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const handleEmailOnlySave = async () => {
    if (!emailOnlyFirstName || !emailOnlyLastName || !emailOnlyEmail) {
      MessagePopup({
        icon: "error",
        title: "Missing Fields",
        message: "Please fill all fields.",
        confirmButtonText: "Ok",
        confirmButtonColor: "#000000",
      });
      return;
    }
    if (!emailRegex.test(emailOnlyEmail)) {
      MessagePopup({
        icon: "error",
        title: "Invalid Email",
        message: "Please enter a valid email address.",
        confirmButtonText: "Ok",
        confirmButtonColor: "#000000",
      });
      return;
    }

    const requestBody = {
      firstName: emailOnlyFirstName,
      lastName: emailOnlyLastName,
      emailId: emailOnlyEmail,
    };

    try {
      const response = await ApiService.post(
        "/v1/admin/userManagement/user-with-only-emails",
        requestBody,
      );
      MessagePopup({
        icon: "success",
        title: "User Added",
        message: "User added successfully.",
        confirmButtonText: "Ok",
        confirmButtonColor: "#000000",
      });
      setEmailOnlyFirstName("");
      setEmailOnlyLastName("");
      setEmailOnlyEmail("");
      handleClose();
      postData();
    } catch (error) {
      MessagePopup({
        icon: "error",
        title: "Addition Failed",
        message: "Failed to add user. Please try again.",
        confirmButtonText: "Ok",
        confirmButtonColor: "#000000",
      });
    }
  };

  useEffect(() => {
    if (selectedRole === "Emailonly") {
      setEmailOnlyFirstName("");
      setEmailOnlyLastName("");
      setEmailOnlyEmail("");
    }
  }, [selectedRole]);

  return (
    <div class="container">
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="subtitle1" align="left" gutterBottom>
              User Management
            </Typography>
            <InfoPopover margin="0 0 3px 0" />
          </Box>
          <ButtonComponent
            bgColor="black"
            color="white"
            label="Add User"
            fullWidth={false}
            onClick={handleOpen}
          />
        </Box>

        {loading ? (
          <Skeleton
            variant="rectangular"
            height={"70vh"}
            animation="pulse"
            style={{ marginBottom: "16px", borderRadius: "8px" }}
          />
        ) : (
          <DataTable
            ref={gridRef}
            columns={columnDefinition}
            rows={filteredData || []}
            tableHeight={"70vh"}
            suppressMovableColumns={true}
            suppressHorizontalScroll={false}
            suppressPaginationPanel={false}
            onSelectionChanged={true}
            pagination={pagination}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            onGridReady={onGrid}
          />
        )}
        <Modal
          open={open}
          handleClose={handleClose}
          maxWidth={selectedRole === "Emailonly" ? "900px" : "100%"}
          height={selectedRole === "Emailonly" ? 250 : 500}
          title="Add User"
          content={
            <div>
              {/* Top Section: Dropdown, Email, Search */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <Grid container spacing={1} marginTop={1}>
                  <Grid item xs={12} sm={4}></Grid>
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      disablePortal
                      options={roletype}
                      size="small"
                      value={selectedRole}
                      onChange={(event, newValue) => setSelectedRole(newValue)}
                      sx={{ width: "100%" }}
                      renderInput={(params) => (
                        <TextField {...params} label="Role *" />
                      )}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={2}
                    display="flex"
                    justifyContent="flex-end"
                  >
                    {selectedRole !== "Emailonly" && (
                      <ButtonComponent
                        bgColor="black"
                        color="white"
                        label="Search"
                        fullWidth={false}
                        onClick={() => searchFunction()}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={2}></Grid>
                </Grid>
              </div>
              {selectedRole === "Emailonly" ? (
                <Box width={"900px"}>
                  <Grid container spacing={2} marginTop={2}>
                    <Grid item xs={12} sm={3.5}>
                      <TextField
                        label="First Name *"
                        value={emailOnlyFirstName}
                        onChange={(e) => setEmailOnlyFirstName(e.target.value)}
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputProps={{
                          style: { fontSize: "13px" },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            transform: "translate(14px, -14px) scale(0.75)",
                          },
                        }}
                        inputProps={{
                          maxLength: 30,
                          placeholder: "Enter your first name",
                        }}
                        helperText={`${emailOnlyFirstName.length}/30 characters`}
                        FormHelperTextProps={{
                          style: {
                            color:
                              emailOnlyFirstName.length === 30 ? "red" : "blue", // Helper text styling
                            fontStyle: "italic",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3.5}>
                      <TextField
                        label="Last Name *"
                        value={emailOnlyLastName}
                        onChange={(e) => setEmailOnlyLastName(e.target.value)}
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputProps={{
                          style: { fontSize: "13px" },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            transform: "translate(14px, -14px) scale(0.75)",
                          },
                        }}
                        inputProps={{
                          maxLength: 30,
                          placeholder: "Enter your last name",
                        }}
                        helperText={`${emailOnlyLastName.length}/30 characters`}
                        FormHelperTextProps={{
                          style: {
                            color:
                              emailOnlyLastName.length === 30 ? "red" : "blue", // Helper text styling
                            fontStyle: "italic",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Email ID *"
                        value={emailOnlyEmail}
                        onChange={(e) => setEmailOnlyEmail(e.target.value)}
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputProps={{
                          style: { fontSize: "13px" },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            transform: "translate(14px, -14px) scale(0.75)",
                          },
                        }}
                        inputProps={{
                          maxLength: 50,
                          placeholder: "Enter your Email",
                        }}
                        helperText={`${emailOnlyEmail.length}/50 characters`}
                        FormHelperTextProps={{
                          style: {
                            color:
                              emailOnlyEmail.length === 50 ? "red" : "blue", // Helper text styling
                            fontStyle: "italic",
                          },
                        }}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={1}
                      display="flex"
                      justifyContent="flex-end"
                      marginTop={0.5}
                    >
                      <ButtonComponent
                        bgColor="black"
                        color="white"
                        label="Save"
                        onClick={handleEmailOnlySave}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <DataTable
                  columns={modalColumns}
                  rows={modalfilteredData || []}
                  pagination={paginationModal}
                  paginationPageSize={paginationModalPageSize}
                  paginationPageSizeSelector={paginationModalPageSizeSelector}
                  tableHeight={350}
                  tablewidth={900}
                  suppressMovableColumns={true}
                  suppressPaginationPanel={false}
                />
              )}
            </div>
          }
        >
          <Box
            sx={{
              maxWidth: "100%",
              height: 450,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Add User
            </Typography>
            {/* Add form or other modal content here */}
            <Button onClick={handleClose}>Close</Button>
          </Box>
        </Modal>
      </Box>
    </div>
  );
}

export default UserManagement;
